import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Flame, Zap, TrendingUp, BarChart3, Target } from 'lucide-react';
import { toast } from 'react-toastify';
import { accountsService } from '../../../services/accountsService';
import Breadcrumb from '../../../components/Common/Breadcrumb';

const getCurrentMonth = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
};

const formatINR = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(v || 0);

const FounderControlReport = () => {
  const navigate = useNavigate();
  const [month, setMonth] = useState(getCurrentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [month]);

  const loadData = async () => {
    setLoading(true);
    try {
      const r = await accountsService.getFounderControlReport({ month });
      if (r?.success) setData(r.data);
      else setData(null);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load founder control report');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Accounts', path: '/accounts', onClick: () => navigate('/accounts') },
    { label: 'Reports', path: '/accounts?tab=reports', onClick: () => navigate('/accounts?tab=reports') },
    { label: 'Founder Control', path: '/accounts/reports/founder-control', isCurrent: true }
  ];

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '22px', margin: '0 0 4px 0' }}>Founder Control Report</h1>
          <p style={{ fontSize: '12px', margin: 0, color: 'var(--color-text-secondary, #5B6478)' }}>Cash, burn, runway, pipeline & budget variance</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label style={{ fontSize: '13px', fontWeight: 500 }}>Month</label>
          <input
            type="month"
            className="form-control"
            style={{ width: 'auto', minWidth: 150 }}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</p>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Row 1: Cash | Monthly Burn | Runway | Projected Runway */}
          <div className="dashboard-metrics-grid" style={{ marginBottom: '16px' }}>
            <div className="metric-card revenue">
              <div className="metric-header"><div className="metric-icon"><DollarSign size={16} /></div></div>
              <div className="metric-value">{formatINR(data.cashTotal)}</div>
              <div className="metric-title">Cash</div>
              <div className="metric-subtitle">Total cash & bank balance</div>
            </div>
            <div className="metric-card churn">
              <div className="metric-header"><div className="metric-icon"><Flame size={16} /></div></div>
              <div className="metric-value">{formatINR(data.monthlyBurn)}</div>
              <div className="metric-title">Monthly Burn</div>
              <div className="metric-subtitle">Approved expenses for month</div>
            </div>
            <div className="metric-card burn">
              <div className="metric-header"><div className="metric-icon"><Zap size={16} /></div></div>
              <div className="metric-value">{data.runwayMonths != null ? `${data.runwayMonths} months` : '—'}</div>
              <div className="metric-title">Runway</div>
              <div className="metric-subtitle">At current burn rate</div>
            </div>
            <div className="metric-card revenue">
              <div className="metric-header"><div className="metric-icon"><TrendingUp size={16} /></div></div>
              <div className="metric-value">{data.projectedRunway != null ? `${data.projectedRunway} months` : '—'}</div>
              <div className="metric-title">Projected Runway</div>
              <div className="metric-subtitle">With 90-day forecast</div>
            </div>
          </div>
          {/* Row 2: Weighted Pipeline | 90-Day Forecast | Budget Variance % | Trailing 30-Day Burn */}
          <div className="dashboard-metrics-grid" style={{ marginBottom: '24px' }}>
            <div className="metric-card customers">
              <div className="metric-header"><div className="metric-icon"><BarChart3 size={16} /></div></div>
              <div className="metric-value">{formatINR(data.weightedPipeline)}</div>
              <div className="metric-title">Weighted Pipeline</div>
              <div className="metric-subtitle">Probability-adjusted</div>
            </div>
            <div className="metric-card burn">
              <div className="metric-header"><div className="metric-icon"><BarChart3 size={16} /></div></div>
              <div className="metric-value">{formatINR(data.probabilityAdjustedForecast)}</div>
              <div className="metric-title">90-Day Forecast</div>
              <div className="metric-subtitle">Expected pipeline closure</div>
            </div>
            <div className="metric-card burn">
              <div className="metric-header"><div className="metric-icon"><Target size={16} /></div></div>
              <div className="metric-value">
                {data.budgetVariancePercent != null ? `${data.budgetVariancePercent >= 0 ? '+' : ''}${data.budgetVariancePercent}%` : '—'}
              </div>
              <div className="metric-title">Budget Variance %</div>
              <div className="metric-subtitle">Actual vs monthly budget</div>
            </div>
            <div className="metric-card churn">
              <div className="metric-header"><div className="metric-icon"><Flame size={16} /></div></div>
              <div className="metric-value">{formatINR(data.trailing30DayBurn)}</div>
              <div className="metric-title">Trailing 30-Day Burn</div>
              <div className="metric-subtitle">Last 30 days approved</div>
            </div>
          </div>
        </>
      )}

      {!loading && !data && (
        <div className="alert alert-info">Select a month to view founder control report.</div>
      )}
    </div>
  );
};

export default FounderControlReport;
